/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

import { ActivityCarouselCard } from '@/components/quality-explorer/carousel/ActivityCarouselCard'
import { Activity } from '@/components/quality-explorer/types'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type ActivityCarouselProps = {
  /** The activities to display in the carousel. */
  activities: Array<Activity>
}

/**
 * A carousel of activities. This component displays a carousel of activities, with the center activity being the largest
 * and most prominent, and the other activities being smaller and semi-transparent. The user can click on the left and
 * right arrows to navigate through the carousel. A click on an activity will also navigate to that activity.
 */
const ActivityCarousel: React.FC<ActivityCarouselProps> = ({ activities }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [containerHeight, setContainerHeight] = useState<undefined | number>(undefined)
  const cardsRef = useRef<(HTMLDivElement | null)[]>([])
  const isSingleCardDisplay = useMediaQuery('(max-width: 1110px)') || activities.length === 1

  /**
   * Update the height of the container to match the height of the tallest card. This function is called when the
   * component mounts and when the window is resized.
   */
  useEffect(() => {
    const updateHeight = () => {
      requestAnimationFrame(() => {
        const heights = cardsRef.current
          .filter((ref): ref is HTMLDivElement => ref !== null)
          .map((ref) => ref.firstElementChild?.scrollHeight ?? ref.scrollHeight)
        const maxHeight = Math.max(...heights, 0)
        console.log('heights', heights, maxHeight)
        if (maxHeight === 0) return
        setContainerHeight(maxHeight)

        // Now that we have the max height, add h-full back
        cardsRef.current
          .filter((ref): ref is HTMLDivElement => ref !== null)
          .forEach((ref) => {
            ref.style.height = '100%'
          })
      })
    }

    updateHeight()
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [])

  /** Handle the click event for the previous button. */
  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + activities.length) % activities.length)
  }

  /** Handle the click event for the next button. */
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % activities.length)
  }

  return (
    <div className={'relative w-full max-w-7xl mx-auto px-4 py-8'}>
      <div className={'relative'} style={{ height: containerHeight }}>
        {activities.map((activity, index) => {
          let offset = (index - currentIndex + activities.length) % activities.length
          if (offset > activities.length / 2) offset -= activities.length

          return (
            <ActivityCarouselCard
              key={activity.id}
              activity={activity}
              offset={offset}
              onClick={() => setCurrentIndex(index)}
              ref={(el) => {
                cardsRef.current[index] = el
              }}
              mode={isSingleCardDisplay ? 1 : 2}
              className={cn(isSingleCardDisplay && 'w-[70%]', !isSingleCardDisplay && 'w-[38%]')}
            />
          )
        })}
      </div>

      <div className={'flex w-full justify-between absolute top-1/2 -translate-y-1/2 -translate-x-4'}>
        <button onClick={handlePrevious} className={'p-2 rounded-full bg-gray-100 hover:bg-gray-200'}>
          <ChevronLeft className={'w-6 h-6'} />
        </button>
        <button onClick={handleNext} className={'p-2 rounded-full bg-gray-100 hover:bg-gray-200'}>
          <ChevronRight className={'w-6 h-6'} />
        </button>
      </div>
    </div>
  )
}

export { ActivityCarousel }
